<div class="p-4 dark:text-app-gray-300">
  <div class="flex items-start justify-between mb-1 gap-4">
    <div class="flex items-center gap-3">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-white">SSO / Iframe Tester</h2>
      <!-- Debug bundle copy — dumps event log + form state + last req/res
           pairs to clipboard as pretty JSON for paste-to-developer debugging.
           Tokens and passwords are redacted before serialization. -->
      <button type="button"
              class="text-[11px] uppercase tracking-wide border border-gray-300 dark:border-[#434863] rounded px-2 py-1
                     text-gray-700 dark:text-gray-300 hover:text-app-teal-100 hover:border-app-teal-100 transition"
              (click)="copyDebugBundle()"
              title="Copy event log + form state + last request/response panels to clipboard for debugging">
        <i class="fa-solid fa-bug mr-1"></i>Copy Debug Bundle
        <span class="ml-1 text-gray-400">({{ debugEvents.length }} evt)</span>
      </button>
      <!-- Clear Form — wipes all form inputs, request/response panels,
           bundle, sid/iframe state, event log, AND the localStorage-
           persisted snapshot. Operator hits this when they want a clean
           slate (different customer, fresh debug session, etc.). -->
      <button type="button"
              class="text-[11px] uppercase tracking-wide border border-gray-300 dark:border-[#434863] rounded px-2 py-1
                     text-gray-700 dark:text-gray-300 hover:text-red-400 hover:border-red-400 transition"
              (click)="clearForm()"
              title="Reset every form field, every request/response panel, the bundle, sid + iframe state, and the event log. Also clears the saved snapshot in localStorage.">
        <i class="fa-solid fa-eraser mr-1"></i>Clear Form
      </button>
    </div>
    <!-- Integration guides — when a customer is selected, surface that
         customer's CustomerAssets entries (the docs we actually ship to
         them) instead of the static cross-customer placeholders. Falls
         back to the static list when no customer is picked yet so the
         strip is never empty. -->
    <div *ngIf="customerAssetLinks.length || integrationGuides.length"
         class="text-xs text-gray-600 dark:text-gray-400 shrink-0">
      <ng-container *ngIf="customerAssetLinks.length; else genericGuides">
        <span class="font-semibold mr-1">{{ selectedPortalCustomer?.name }} docs:</span>
        <ng-container *ngFor="let g of customerAssetLinks; let last = last">
          <a [href]="g.href" target="_blank" rel="noopener"
             class="text-app-teal-100 hover:underline"
             [attr.title]="'Open ' + g.label + ' (visibility: ' + g.visibility.join(', ') + ')'">
            <i class="fa-solid mr-1"
               [class.fa-file-pdf]="g.filename.endsWith('.pdf')"
               [class.fa-file-lines]="g.filename.endsWith('.md')"
               [class.fa-globe]="g.filename.endsWith('.html')"
               [class.fa-file]="!g.filename.endsWith('.html') && !g.filename.endsWith('.md') && !g.filename.endsWith('.pdf')"></i>{{ g.label }}
          </a>
          <span *ngIf="!last" class="mx-1 text-gray-400">·</span>
        </ng-container>
      </ng-container>
      <ng-template #genericGuides>
        <span class="font-semibold mr-1">Integration guides:</span>
        <ng-container *ngFor="let g of integrationGuides; let last = last">
          <a [href]="g.href" target="_blank" rel="noopener"
             class="text-app-teal-100 hover:underline"
             [attr.download]="g.href.endsWith('.pdf') ? g.href.split('/').pop() : null"
             [attr.title]="'Open ' + g.label">
            <i class="fa-solid mr-1"
               [class.fa-file-pdf]="g.href.endsWith('.pdf')"
               [class.fa-file-lines]="g.href.endsWith('.md')"
               [class.fa-globe]="g.href.endsWith('.html')"></i>{{ g.label }}
          </a>
          <span *ngIf="!last" class="mx-1 text-gray-400">·</span>
        </ng-container>
      </ng-template>
    </div>
  </div>
  <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
    Step-by-step exerciser for the sid handoff API. Three auth paths: admin-cookie (test as yourself),
    customer-credentials (simulate an end-user's iframe / SSO integration), or portal-JWTSSO
    (exercise a customer portal's inbound HS256 handoff — driven by <code>F2-ADMIN.Customers.sso_portal</code>).
  </p>

  <!-- STEP 1: Customer (company) selection. Customer-first so the rest of
       the form adapts to what THIS customer's config actually supports —
       no fiddling with options the customer doesn't accept. Source:
       /admin/customer-directory/sso-portal/list. -->
  <div class="border border-gray-200 dark:border-[#434863] rounded-md mb-4">
    <div class="px-3 py-2 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863] flex items-center gap-2">
      <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-app-teal-100 text-white text-xs font-bold">1</span>
      <span class="text-sm font-semibold text-gray-900 dark:text-white">Pick the customer (company)</span>
      <span class="text-xs text-gray-500 ml-auto">
        <ng-container *ngIf="selectedPortalCustomer as c">
          <i class="fa-solid fa-check text-green-500 mr-1"></i>{{ c.name }} ({{ c.slug }})
        </ng-container>
        <ng-container *ngIf="!selectedPortalCustomer">— none selected —</ng-container>
      </span>
    </div>
    <div class="p-3 space-y-3">
      <div *ngIf="portalCustomers.length === 0" class="text-xs text-amber-500">
        No customers have <code>sso_portal.enabled = true</code>. Configure one in
        <a routerLink="/admin/customer-directory" class="underline">Customer Directory</a> first.
      </div>
      <div *ngIf="portalCustomers.length > 0">
        <select [(ngModel)]="selectedPortalSlug" (change)="onPortalCustomerChange()"
                class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white">
          <option [value]="''" disabled>— pick a company —</option>
          <option *ngFor="let c of portalCustomers" [value]="c.slug">{{ c.name }} ({{ c.slug }})</option>
        </select>
      </div>

      <!-- Customer info + handoff mode badge — visible at a glance so the
           operator knows what shape they're testing against. -->
      <div *ngIf="selectedPortalCustomer as cust" class="text-xs text-gray-600 dark:text-gray-400 grid grid-cols-2 gap-x-4 gap-y-1 border border-gray-200 dark:border-[#434863] rounded p-2 bg-gray-50 dark:bg-app-black-100">
        <div class="col-span-2 flex items-center gap-2 flex-wrap">
          <span class="font-semibold text-gray-700 dark:text-gray-300">Handoff URL shape:</span>
          <span *ngIf="!cust.mta_federation && cust.handoff_mode === 'token_query'"
                class="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-amber-200 dark:bg-amber-900/60 text-amber-900 dark:text-amber-200">
            <i class="fa-solid fa-triangle-exclamation mr-1"></i>JWT in <code>?token=</code> (signed with shared secret)
          </span>
          <span *ngIf="!cust.mta_federation && cust.handoff_mode !== 'token_query'"
                class="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-emerald-200 dark:bg-emerald-900/60 text-emerald-900 dark:text-emerald-200">
            <i class="fa-solid fa-shield-halved mr-1"></i>Opaque SID (<code>?sid=</code>) — single-use, redeemed
          </span>
          <span *ngIf="cust.mta_federation"
                class="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-indigo-200 dark:bg-indigo-900/60 text-indigo-900 dark:text-indigo-200">
            <i class="fa-solid fa-share-nodes mr-1"></i>MTA federation only — no <code>sso_portal</code>. Pick <strong>MTA endpoint</strong> below.
          </span>
          <span *ngIf="cust.sid_redeem_grace_seconds && cust.sid_redeem_grace_seconds > 0"
                class="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-blue-200 dark:bg-blue-900/60 text-blue-900 dark:text-blue-200"
                title="SID stays redeemable for this many seconds after first redeem — survives iframe-refresh re-mount">
            <i class="fa-solid fa-rotate-right mr-1"></i>{{ cust.sid_redeem_grace_seconds }}s redeem grace
          </span>
          <label class="inline-flex items-center gap-1 ml-auto px-1.5 py-0.5 rounded border cursor-pointer select-none" [ngClass]="cust.debug_mode ? 'bg-yellow-100 dark:bg-yellow-500/25 border-yellow-500' : 'border-gray-300 dark:border-app-black-100'" [title]="portalDebugTooltip(cust)">
            <input type="checkbox" [checked]="!!cust.debug_mode" [disabled]="!!portalDebugTogglePending" (change)="onPortalDebugToggle($any($event.target).checked)" class="w-3 h-3" />
            <span class="text-[11px] font-semibold">
              <i class="fa-solid fa-bug" [ngClass]="cust.debug_mode ? 'text-yellow-700 dark:text-yellow-300' : 'text-gray-400'"></i>
              Debug
              <i *ngIf="portalDebugTogglePending" class="fa-solid fa-spinner fa-spin ml-1 opacity-70"></i>
            </span>
          </label>
        </div>
        <div><span class="font-semibold text-gray-700 dark:text-gray-300">Expected <code>type</code>:</span> <code class="text-app-teal-100">{{ cust.expected_type || '—' }}</code></div>
        <div><span class="font-semibold text-gray-700 dark:text-gray-300">Scanner id:</span> <code class="text-app-teal-100">{{ cust.scanner_id || '—' }}</code></div>
        <div class="col-span-2 break-all"><span class="font-semibold text-gray-700 dark:text-gray-300">Scanners.Url:</span>
          <code class="text-app-teal-100">{{ portalScannerRow?.Url || '— missing — seed F2-ADMIN.Scanners.' + cust.scanner_id + '.Url' }}</code>
        </div>
        <div class="col-span-2"><span class="font-semibold text-gray-700 dark:text-gray-300">Customer host override:</span> <code class="text-app-teal-100">{{ cust.scanner_host || '— (use Scanners.Url host)' }}</code></div>
        <div class="col-span-2 break-all"><span class="font-semibold text-gray-700 dark:text-gray-300">Expected redirect:</span> <code class="text-app-teal-100">{{ portalExpectedRedirect || '—' }}</code></div>
        <div class="col-span-2"><span class="font-semibold text-gray-700 dark:text-gray-300">Required claims:</span> <code class="text-app-teal-100">{{ cust.required_claims.join(', ') }}</code></div>
        <div>
          <span class="font-semibold text-gray-700 dark:text-gray-300">Active secrets:</span>
          <span [class.text-amber-500]="cust.active_secrets_count === 0">{{ cust.active_secrets_count }}</span>
          <span *ngIf="cust.newest_fingerprint" class="ml-1 text-gray-500">· newest fp: <code class="text-app-teal-100">{{ cust.newest_fingerprint }}</code></span>
        </div>
        <div>
          <span class="font-semibold text-gray-700 dark:text-gray-300">Embed modes:</span>
          <span *ngFor="let m of cust.embed_modes; let last = last" class="inline-block ml-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-100 dark:bg-app-blue-100/40 text-blue-900 dark:text-blue-200">{{ m }}</span>
          <span *ngIf="!cust.embed_modes?.length" class="ml-1 text-gray-500">—</span>
        </div>
        <div *ngIf="cust.docs_url" class="col-span-2">
          <span class="font-semibold text-gray-700 dark:text-gray-300">Integration guide:</span>
          <a [href]="cust.docs_url" target="_blank" rel="noopener"
             class="ml-1 text-app-teal-100 hover:underline font-semibold">
            <i class="fa-solid mr-1"
               [class.fa-globe]="cust.docs_url.endsWith('.html')"
               [class.fa-file-pdf]="cust.docs_url.endsWith('.pdf')"
               [class.fa-file-lines]="cust.docs_url.endsWith('.md')"></i>Open {{ cust.name }} guide
            <i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[10px] opacity-70"></i>
          </a>
          <span class="ml-2 text-[10px] text-gray-500 font-mono">{{ cust.docs_url }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- STEP 2: Auth path (was Step 1). Gated by Step 1: until a customer
       is picked, the radios stay disabled so the operator can't try
       things that don't work. -->
  <div class="border border-gray-200 dark:border-[#434863] rounded-md mb-4"
       [class.opacity-50]="!selectedPortalCustomer">
    <div class="px-3 py-2 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863] flex items-center gap-2">
      <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-app-teal-100 text-white text-xs font-bold">2</span>
      <span class="text-sm font-semibold text-gray-900 dark:text-white">Choose authentication path</span>
      <span class="text-xs text-gray-500 ml-auto">
        <ng-container *ngIf="authPath === 'admin'">admin cookie</ng-container>
        <ng-container *ngIf="authPath === 'customer' && custBundle"><i class="fa-solid fa-check text-green-500 mr-1"></i>customer logged in: {{ custBundle.user?.email || custEmail }}</ng-container>
        <ng-container *ngIf="authPath === 'customer' && !custBundle">customer (needs login)</ng-container>
        <ng-container *ngIf="isPortalPath && selectedPortalCustomer"><i class="fa-solid fa-check text-green-500 mr-1"></i>portal: {{ selectedPortalCustomer.name }} ({{ authPath === 'portal-sid' ? '?sid=' : '?token=' }})</ng-container>
        <ng-container *ngIf="isPortalPath && !selectedPortalCustomer">portal (needs customer)</ng-container>
        <ng-container *ngIf="isMtaPath && selectedPortalCustomer"><i class="fa-solid fa-check text-green-500 mr-1"></i>mta endpoint: {{ selectedPortalCustomer.name }}</ng-container>
        <ng-container *ngIf="isMtaPath && !selectedPortalCustomer">mta endpoint (needs customer)</ng-container>
      </span>
    </div>
    <div class="p-3">
      <div class="flex gap-3 mb-3 flex-wrap">
        <label class="text-sm text-gray-900 dark:text-white flex items-center gap-2 cursor-pointer">
          <input type="radio" [(ngModel)]="authPath" value="admin" [disabled]="!selectedPortalCustomer" />
          <span><strong>Admin (cookie-authed)</strong> — mint as your own admin identity via <code class="text-app-teal-100">/auth/mint-scanner-sid-cookie</code></span>
        </label>
        <label class="text-sm text-gray-900 dark:text-white flex items-center gap-2 cursor-pointer">
          <input type="radio" [(ngModel)]="authPath" value="customer" [disabled]="!selectedPortalCustomer" />
          <span><strong>Customer (credentials)</strong> — login as a user, then mint via <code class="text-app-teal-100">/auth/mint-scanner-sid</code></span>
        </label>
        <label class="text-sm flex items-center gap-2 cursor-pointer"
               [class.text-gray-900]="!selectedPortalCustomer?.mta_federation && selectedPortalCustomer"
               [class.dark:text-white]="!selectedPortalCustomer?.mta_federation && selectedPortalCustomer"
               [class.text-gray-400]="!selectedPortalCustomer || !!selectedPortalCustomer?.mta_federation"
               [class.dark:text-gray-500]="!selectedPortalCustomer || !!selectedPortalCustomer?.mta_federation"
               [class.cursor-not-allowed]="!selectedPortalCustomer || !!selectedPortalCustomer?.mta_federation">
          <input type="radio" [(ngModel)]="authPath" value="portal-sid"
                 [disabled]="!selectedPortalCustomer || !!selectedPortalCustomer?.mta_federation" />
          <span>
            <strong>Portal (JWTSSO) → ?sid=</strong> — POST to <code class="text-app-teal-100">/auth/validate-portal-token/:customer</code> (forces sid mode; works for every sso_portal customer)
            <span *ngIf="selectedPortalCustomer?.mta_federation" class="block text-[11px] italic text-gray-500 dark:text-gray-500">
              <i class="fa-solid fa-ban mr-1"></i>{{ selectedPortalCustomer?.name }} has MTA federation only — no <code>sso_portal</code>. Pick <strong>MTA endpoint</strong> below.
            </span>
          </span>
        </label>
        <label class="text-sm flex items-center gap-2 cursor-pointer"
               [class.text-gray-900]="portalCanTestTokenMode && selectedPortalCustomer && !selectedPortalCustomer?.mta_federation"
               [class.dark:text-white]="portalCanTestTokenMode && selectedPortalCustomer && !selectedPortalCustomer?.mta_federation"
               [class.text-gray-400]="!portalCanTestTokenMode || !selectedPortalCustomer || !!selectedPortalCustomer?.mta_federation"
               [class.dark:text-gray-500]="!portalCanTestTokenMode || !selectedPortalCustomer || !!selectedPortalCustomer?.mta_federation"
               [class.cursor-not-allowed]="!portalCanTestTokenMode || !selectedPortalCustomer || !!selectedPortalCustomer?.mta_federation">
          <input type="radio" [(ngModel)]="authPath" value="portal-token"
                 [disabled]="!selectedPortalCustomer || !portalCanTestTokenMode || !!selectedPortalCustomer?.mta_federation" />
          <span>
            <strong>Portal (JWTSSO) → ?token=</strong> — POST to <code class="text-app-teal-100">/auth/validate-customer-token</code> (honors <code>handoff_mode</code>; ?token= URL only when customer has <code>handoff_mode='token_query'</code>)
            <span *ngIf="selectedPortalCustomer && !portalCanTestTokenMode && !selectedPortalCustomer?.mta_federation" class="block text-[11px] italic text-gray-500 dark:text-gray-500">
              <i class="fa-solid fa-ban mr-1"></i>{{ selectedPortalCustomer.name }} is sid-only — this customer's <code>sso_portal.handoff_mode</code> isn't <code>token_query</code>.
            </span>
          </span>
        </label>
        <!-- MTA endpoint: shared /rest/auth/validate-mta-token federated path.
             Enabled when the customer has mta_endpoint_allowed=true or is
             MTA itself (implicit). Signs a JWT with env.MTA_JWT_SECRET_KEY
             server-side, then POSTs to the production validate endpoint. -->
        <label class="text-sm flex items-center gap-2 cursor-pointer"
               [class.text-gray-900]="portalCanTestMtaEndpoint && selectedPortalCustomer"
               [class.dark:text-white]="portalCanTestMtaEndpoint && selectedPortalCustomer"
               [class.text-gray-400]="!portalCanTestMtaEndpoint || !selectedPortalCustomer"
               [class.dark:text-gray-500]="!portalCanTestMtaEndpoint || !selectedPortalCustomer"
               [class.cursor-not-allowed]="!portalCanTestMtaEndpoint || !selectedPortalCustomer">
          <input type="radio" [(ngModel)]="authPath" value="mta-endpoint"
                 [disabled]="!selectedPortalCustomer || !portalCanTestMtaEndpoint" />
          <span>
            <strong>MTA endpoint</strong> — POST to <code class="text-app-teal-100">/auth/validate-mta-token</code> (federated; server signs test JWT with <code>MTA_JWT_SECRET_KEY</code>)
            <span *ngIf="selectedPortalCustomer && !portalCanTestMtaEndpoint" class="block text-[11px] italic text-gray-500 dark:text-gray-500">
              <i class="fa-solid fa-ban mr-1"></i>{{ selectedPortalCustomer.name }} isn't allowed on the MTA endpoint — set <code>mta_endpoint_allowed=true</code> on the Customer doc first.
            </span>
          </span>
        </label>
      </div>
      <p *ngIf="!selectedPortalCustomer" class="text-[11px] text-amber-500 mb-2">
        <i class="fa-solid fa-arrow-up mr-1"></i>Pick a customer in Step 1 first &mdash; auth paths are customer-scoped.
      </p>

      <!-- Portal-path form (customer info already shown in Step 1) -->
      <div *ngIf="isPortalPath" class="mt-2 border-t border-gray-200 dark:border-[#434863] pt-3 space-y-3">
        <div *ngIf="selectedPortalCustomer as cust" class="space-y-3">

          <!-- v2: allow_create_account toggle. Positioned ABOVE the identity
               inputs because it gates whether those fields are even needed —
               OFF = lookup-only (identity fields aren't required); ON = F2
               can auto-provision the user from the JWT identity (fields below
               become required). Operator should choose the mode FIRST, then
               type identity. Only renders when customer config declares the
               claim name. -->
          <div *ngIf="portalSupportsAllowCreateAccount"
               class="border border-amber-200 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-900/20 rounded p-2 mb-2">
            <label class="flex items-center gap-2 cursor-pointer text-sm text-gray-900 dark:text-white">
              <input type="checkbox" [(ngModel)]="portalAllowCreateAccount" />
              <span class="font-semibold">Allow create account</span>
              <code class="text-[10px] text-gray-500 normal-case">payload.allow_create_account</code>
            </label>
            <p class="text-[11px] text-gray-600 dark:text-gray-400 mt-1">
              <ng-container *ngIf="portalAllowCreateAccount">
                <strong>ON</strong> &mdash; F2 will mint a Cognito user from the JWT identity if the email is unknown. Provide first/last name<ng-container *ngIf="portalSupportsPhone"> + phone</ng-container> below.
              </ng-container>
              <ng-container *ngIf="!portalAllowCreateAccount">
                <strong>OFF</strong> &mdash; lookup-only. F2 returns valid:false if the email isn't already in Cognito. Identity fields not needed.
              </ng-container>
            </p>
          </div>

          <!-- Identity inputs — shape adapts to identity_mapping -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <ng-container *ngIf="!portalUsesEmailTemplate">
              <div>
                <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Email (JWT <code>{{ cust.identity_mapping?.email || 'email' }}</code> claim)
                </label>
                <input type="email" autocomplete="off" [(ngModel)]="portalEmail"
                       placeholder="user@example.com"
                       class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
              </div>
            </ng-container>
            <ng-container *ngIf="portalUsesEmailTemplate">
              <div>
                <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  <code>{{ portalEmailSourceClaim }}</code> (synthesised into <code>{{ cust.identity_mapping?.email_template }}</code>)
                </label>
                <input type="text" autocomplete="off" [(ngModel)]="portalEmailSourceValue"
                       placeholder="abc123"
                       class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
              </div>
            </ng-container>

            <ng-container *ngIf="!portalUsesSeparatedNames && portalRequiresIdentity">
              <div>
                <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Name (JWT <code>{{ cust.identity_mapping?.name || 'name' }}</code> claim — split on first space)
                </label>
                <input type="text" autocomplete="off" [(ngModel)]="portalName"
                       placeholder="Jane Trader"
                       class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
              </div>
            </ng-container>
            <ng-container *ngIf="portalUsesSeparatedNames && portalRequiresIdentity">
              <div>
                <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">First Name (<code>{{ cust.identity_mapping?.first_name || 'first_name' }}</code>)</label>
                <input type="text" autocomplete="off" [(ngModel)]="portalFirstName"
                       placeholder="Jane"
                       class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
              </div>
              <div>
                <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Last Name (<code>{{ cust.identity_mapping?.last_name || 'last_name' }}</code>)</label>
                <input type="text" autocomplete="off" [(ngModel)]="portalLastName"
                       placeholder="Trader"
                       class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
              </div>
            </ng-container>
            <!-- v2: optional phone, only when customer supports it AND identity is required. -->
            <ng-container *ngIf="portalSupportsPhone && portalRequiresIdentity">
              <div>
                <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Phone (<code>{{ cust.identity_mapping?.phone || 'phone' }}</code>) &mdash; E.164 e.g. +15555555555
                </label>
                <input type="tel" autocomplete="off" [(ngModel)]="portalPhone"
                       placeholder="+15555555555"
                       class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
              </div>
            </ng-container>
          </div>

          <!-- Scanner field — JWT's top-level `scanner` value. When set,
               backend resolves the redirect URL to that scanner; when
               blank, lands on the customer's members page. Uses [ngClass]
               (not [class.X]) so Tailwind arbitrary-value classes like
               `dark:border-[#434863]` parse cleanly — they trip Angular's
               bracket-matcher inside [class.] bindings. -->
          <div class="rounded p-2 border"
               [ngClass]="portalScannerSlug.trim()
                 ? 'border-app-teal-100 bg-app-teal-100/10'
                 : 'border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100'">
            <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Scanner (<code>{{ cust.scanner_selection?.scanner_claim || 'scanner' }}</code>)
              &mdash; <span class="normal-case text-gray-500">JWT field; routes to a specific scanner when present</span>
            </label>
            <div class="flex gap-2 items-stretch">
              <select [(ngModel)]="portalScannerSlug"
                      class="flex-1 bg-white border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white">
                <option value="">— (none — return to members page) —</option>
                <!-- Formal Name first, slug de-emphasized in parens. Native
                     <option> can't style parts, but the visual hierarchy
                     "Big Name (small-slug)" reads correctly even at one
                     color. Operator sees what the scanner IS, not just
                     the routing key. -->
                <option *ngFor="let s of portalCustomerScanners" [value]="s.id">{{ s.name }} ({{ s.id }})</option>
              </select>
              <input type="text" [(ngModel)]="portalScannerSlug"
                     placeholder="or type alias / custom id"
                     class="flex-1 bg-white border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm font-mono dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
              <button type="button" class="dark-btn dark-btn-sm whitespace-nowrap"
                      [disabled]="!portalScannerSlug"
                      (click)="portalScannerSlug = ''"
                      title="Clear scanner — landing reverts to members page">
                <i class="fa-solid fa-xmark mr-1"></i>Clear
              </button>
            </div>
            <p class="text-[11px] mt-1"
               [ngClass]="portalScannerSlug.trim() ? 'text-app-teal-100' : 'text-gray-500 dark:text-gray-400'">
              <ng-container *ngIf="!portalScannerSlug.trim()">
                Blank &mdash; <strong>will return the members page</strong> (<code>https://{{ cust.scanner_host || '&lt;customer-host&gt;' }}/?sid=&hellip;</code>).
              </ng-container>
              <ng-container *ngIf="portalScannerSlug.trim()">
                <i class="fa-solid fa-arrow-right mr-1"></i>Will land directly on <code>{{ portalScannerSlug.trim() }}</code>'s scanner page.
              </ng-container>
            </p>
          </div>

          <!-- Scanner picker (from_jwt mode only) — multi-select.
               First selected = JWT.scanner (landing). All selected = JWT.scanners
               (entitlement list). Matches the multi-scanner reality where one
               user often has access to several. -->
          <div *ngIf="cust.scanner_selection?.mode === 'from_jwt'">
            <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Scanners (1st checked = JWT <code>{{ cust.scanner_selection?.scanner_claim || 'scanner' }}</code> landing &middot; all checked = JWT <code>scanners[]</code> entitlement)
            </label>
            <div *ngIf="portalScannerOptions.length === 0" class="text-xs text-amber-500 italic p-2 border border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 rounded">
              No <code>SsoAliases</code> set for this customer's scanners. Configure on the Scanners admin first.
            </div>
            <div *ngIf="portalScannerOptions.length > 0" class="flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100 rounded">
              <label *ngFor="let o of portalScannerOptions" class="text-xs text-gray-900 dark:text-white flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" [(ngModel)]="portalScannerAliases[o.value]" />
                <code>{{ o.label }}</code>
              </label>
            </div>
            <p class="text-[10px] text-gray-500 mt-1" *ngIf="portalSelectedScannerAliases.length > 0">
              Landing scanner: <code class="text-app-teal-100">{{ portalSelectedScannerAliases[0] }}</code>
            </p>
          </div>

          <!-- Memberships (only when allowed_memberships has entries) -->
          <div *ngIf="cust.allowed_memberships.length > 0">
            <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Memberships (must intersect <code>allowed_memberships</code>)</label>
            <div class="flex flex-wrap gap-2 p-2 border border-gray-300 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100 rounded">
              <label *ngFor="let m of cust.allowed_memberships" class="text-xs text-gray-900 dark:text-white flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" [(ngModel)]="portalMemberships[m]" />
                <code>{{ m }}</code>
              </label>
            </div>
          </div>

          <!-- Extra claims (live_data, scanners, etc.) -->
          <div>
            <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Extra claims JSON (overrides — for fields outside the schema)
            </label>
            <textarea rows="2" [(ngModel)]="portalExtrasJson"
                      placeholder='&#123;"some_extra_claim": "value"&#125;'
                      class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-xs font-mono dark:bg-app-black-100 dark:border-[#434863] dark:text-white"></textarea>
          </div>

          <!-- exp control sits with the preview so the operator can see
               iat/exp change as they tweak it. -->
          <div class="border-t border-gray-200 dark:border-[#434863] pt-3">
            <div class="flex items-end justify-between gap-3 mb-2">
              <label class="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <i class="fa-solid fa-eye text-app-teal-100"></i>JWT payload preview
                <span class="text-[10px] normal-case text-gray-500">(live — exactly what the server will sign)</span>
              </label>
              <label class="text-[10px] text-gray-500 dark:text-gray-400 flex items-center gap-1.5 whitespace-nowrap">
                <span class="uppercase tracking-wide">exp (s)</span>
                <input type="number" min="5" max="300" [(ngModel)]="portalExpSeconds"
                       class="w-20 bg-gray-50 border border-gray-300 text-gray-900 rounded px-1.5 py-0.5 text-xs dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
                <!-- Paste JWT — reads the clipboard, decodes the middle
                     segment, and populates the identity form fields
                     (email/first_name/last_name/phone/allow_create_account/
                     scanner). Useful for reproducing a real customer JWT
                     from a copied URL or log line: paste it, fields fill in,
                     operator can tweak and re-sign. Base64-encoded email +
                     phone are auto-decoded back to plaintext for the form.
                     -->
                <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-1.5 py-0.5"
                        (click)="pasteJwtAndPopulate()"
                        title="Read a JWT from the clipboard, decode it, and populate the form fields above. Smart base64-decode for email + phone.">
                  <i class="fa-solid fa-paste mr-1"></i>paste JWT
                </button>
                <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100 border border-gray-300 dark:border-[#434863] rounded px-1.5 py-0.5"
                        [disabled]="!jwtPreviewJson"
                        (click)="copyText(jwtPreviewJson)"
                        title="Copy JWT payload JSON">
                  <i class="fa-regular fa-copy mr-1"></i>copy
                </button>
              </label>
            </div>
            <pre class="bg-gray-50 dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded p-2 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto max-h-48">{{ jwtPreviewJson || '— select a customer + fill in identity fields to preview —' }}</pre>
          </div>

          <!-- Claims (perms): Configure button paired with its value box.
               Textarea is read-only — canonical edit is via the popup,
               schema-driven. Operator sees the JSON that will be sent. -->
          <div>
            <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
              Claims (defined by customer's <code>claims_schema</code>)
            </label>
            <div class="flex gap-2 items-stretch">
              <button type="button" class="dark-btn whitespace-nowrap self-start"
                      (click)="openPermsPopup()">
                <i class="fa-solid fa-sliders mr-1 dark:text-blue-400"></i>
                <ng-container *ngIf="selectedPortalCustomer?.claims_schema">Configure&hellip;</ng-container>
                <ng-container *ngIf="!selectedPortalCustomer?.claims_schema">No schema</ng-container>
              </button>
              <textarea readonly rows="3"
                        class="flex-1 bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-xs font-mono dark:bg-app-black-100 dark:border-[#434863] dark:text-white"
                        [value]="portalPermsJson"></textarea>
            </div>
            <p class="text-[10px] text-gray-500 mt-1" *ngIf="!selectedPortalCustomer?.claims_schema">
              Customer has no <code>claims_schema</code> seeded &mdash; sends as <code>&#123;&#125;</code>. Configure in Customer Directory to enable the guided editor.
            </p>
          </div>
        </div>

        <!-- Sign-token req + res (Step 3a in the portal flow) -->
        <div *ngIf="signTokenReq || signing_token" class="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
          <div class="border border-gray-200 dark:border-[#434863] rounded-md">
            <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
              <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Sign-Test-Token Request (server signs HS256)</span>
              <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" (click)="copyText(signTokenReq?.body)" title="Copy request body">
                <i class="fa-regular fa-copy mr-1"></i>copy
              </button>
            </div>
            <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ signTokenReq ? signTokenReq.method + ' ' + signTokenReq.url + '\n\n' + prettyJson(signTokenReq.body) : '—' }}</pre>
          </div>
          <div class="border border-gray-200 dark:border-[#434863] rounded-md">
            <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
              <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Sign-Test-Token Response (signed JWT + payload)</span>
              <div class="flex items-center gap-2">
                <span *ngIf="signTokenRes" class="text-[10px] uppercase tracking-wide"
                      [class.text-green-500]="signTokenRes.ok" [class.text-red-500]="!signTokenRes.ok">
                  HTTP {{ signTokenRes.status }} · {{ signTokenRes.durationMs }}ms
                </span>
                <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" [disabled]="!signTokenRes" (click)="copyText(signTokenRes?.body)" title="Copy response body">
                  <i class="fa-regular fa-copy mr-1"></i>copy
                </button>
              </div>
            </div>
            <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ signTokenRes ? prettyJson(signTokenRes.body) : '—' }}</pre>
            <div *ngIf="signTokenRes?.error" class="px-3 pb-2 text-xs text-red-500">{{ signTokenRes!.error }}</div>
          </div>
        </div>
      </div>

      <!-- MTA-endpoint form -->
      <div *ngIf="isMtaPath" class="mt-2 border-t border-gray-200 dark:border-[#434863] pt-3 space-y-3">
        <div *ngIf="selectedPortalCustomer as cust" class="space-y-3">
          <div class="text-[11px] text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
            <i class="fa-solid fa-share-nodes"></i>
            <span>Federated MTA endpoint —
              backend routes on <code>Scanners.&lt;scanner&gt;.Client</code>, gated by <code>mta_endpoint_allowed</code> (or <code>_id==='mta'</code>).
              JWT is signed with <code>env.MTA_JWT_SECRET_KEY</code> — <strong>not</strong> this customer's sso_portal secret.</span>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                <code>user_identifier</code>
              </label>
              <input type="text" autocomplete="off" [(ngModel)]="mtaUserIdentifier"
                     placeholder="sso-tester"
                     class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
              <p class="text-[10px] text-gray-500 mt-1">Server synthesises email as <code>&lt;user_identifier&gt;&#64;mta.com</code>.</p>
            </div>
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                <code>access_tier</code>
              </label>
              <input type="number" min="0" step="1" [(ngModel)]="mtaAccessTier"
                     class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
              <p class="text-[10px] text-gray-500 mt-1">Numeric entitlement tier — 1 = paying, 0 = free trial, etc.</p>
            </div>
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                <code>scanner</code> claim
              </label>
              <select [(ngModel)]="mtaScannerClaim"
                      class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white">
                <option [value]="''" disabled>— pick a scanner —</option>
                <ng-container *ngIf="portalCustomerScanners.length > 0">
                  <option *ngFor="let s of portalCustomerScanners" [value]="s.id">{{ s.name }} ({{ s.id }})</option>
                </ng-container>
                <ng-container *ngIf="portalCustomerScanners.length === 0 && cust.scanner_ids?.length">
                  <option *ngFor="let sid of cust.scanner_ids" [value]="sid">{{ sid }}</option>
                </ng-container>
              </select>
              <p class="text-[10px] text-gray-500 mt-1">Backend resolves via <code>Scanners._id</code> or <code>Scanners.aliases[]</code>. Owning customer comes from <code>Scanners.Client</code>.</p>
            </div>
            <div>
              <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                Expires in (seconds) — clamped [5, 900]
              </label>
              <input type="number" min="5" max="900" step="5" [(ngModel)]="mtaExpSeconds"
                     class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
            </div>
          </div>

          <!-- Live JWT preview -->
          <div class="border border-gray-200 dark:border-[#434863] rounded-md">
            <div class="px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between">
              <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">JWT payload preview (before signing)</span>
              <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" (click)="copyText(mtaJwtPreviewJson)">
                <i class="fa-regular fa-copy mr-1"></i>copy
              </button>
            </div>
            <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ mtaJwtPreviewJson }}</pre>
          </div>

          <!-- Sign button -->
          <div class="flex items-center gap-2 flex-wrap">
            <button type="button" class="dark-btn"
                    [class.dark-btn-disabled]="!mtaReady || signing_mta_jwt"
                    [disabled]="!mtaReady || signing_mta_jwt"
                    (click)="signMtaTestJwt()">
              <i class="fa-solid fa-signature mr-1 dark:text-amber-400"></i>
              <ng-container *ngIf="!signing_mta_jwt">Sign test JWT</ng-container>
              <ng-container *ngIf="signing_mta_jwt">Signing…</ng-container>
            </button>
            <span *ngIf="mtaSignedJwt" class="text-[11px] text-green-500 flex items-center gap-1">
              <i class="fa-solid fa-check"></i>
              signed · fp:<code class="text-app-teal-100">{{ mtaSignFingerprint || '—' }}</code> ·
              {{ mtaSignedJwt.length }} chars
            </span>
          </div>

          <!-- Sign request/response -->
          <div *ngIf="mtaSignReq || signing_mta_jwt" class="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div class="border border-gray-200 dark:border-[#434863] rounded-md">
              <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
                <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Sign-MTA-JWT Request</span>
                <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" (click)="copyText(mtaSignReq?.body)"><i class="fa-regular fa-copy mr-1"></i>copy</button>
              </div>
              <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ mtaSignReq ? mtaSignReq.method + ' ' + mtaSignReq.url + '\n\n' + prettyJson(mtaSignReq.body) : '—' }}</pre>
            </div>
            <div class="border border-gray-200 dark:border-[#434863] rounded-md">
              <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
                <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Sign-MTA-JWT Response</span>
                <div class="flex items-center gap-2">
                  <span *ngIf="mtaSignRes" class="text-[10px] uppercase tracking-wide"
                        [class.text-green-500]="mtaSignRes.ok" [class.text-red-500]="!mtaSignRes.ok">
                    HTTP {{ mtaSignRes.status }} · {{ mtaSignRes.durationMs }}ms
                  </span>
                  <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" [disabled]="!mtaSignRes" (click)="copyText(mtaSignRes?.body)"><i class="fa-regular fa-copy mr-1"></i>copy</button>
                </div>
              </div>
              <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ mtaSignRes ? prettyJson(mtaSignRes.body) : '—' }}</pre>
              <div *ngIf="mtaSignRes?.error" class="px-3 pb-2 text-xs text-red-500">{{ mtaSignRes!.error }}</div>
            </div>
          </div>

          <!-- Validate button — standalone, doesn't chain from sign. -->
          <div class="flex items-center gap-2 flex-wrap">
            <button type="button" class="dark-btn"
                    [class.dark-btn-disabled]="!mtaSignedJwt || minting"
                    [disabled]="!mtaSignedJwt || minting"
                    (click)="validateMtaToken()">
              <i class="fa-solid fa-plane-departure mr-1 dark:text-blue-400"></i>
              <ng-container *ngIf="!minting">Validate (POST to /auth/validate-mta-token)</ng-container>
              <ng-container *ngIf="minting">Validating…</ng-container>
            </button>
            <span *ngIf="!mtaSignedJwt" class="text-[11px] text-gray-500 italic">Sign a JWT first.</span>
          </div>
        </div>
      </div>

      <!-- Customer-path login form -->
      <div *ngIf="authPath === 'customer'" class="mt-2 border-t border-gray-200 dark:border-[#434863] pt-3">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
          <div>
            <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Email</label>
            <input type="email" autocomplete="off" [(ngModel)]="custEmail"
                   placeholder="user@example.com"
                   [disabled]="!selectedScannerId"
                   class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
          </div>
          <div>
            <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Password</label>
            <input type="password" autocomplete="off" [(ngModel)]="custPassword"
                   (keyup.enter)="customerLogin()"
                   [disabled]="!selectedScannerId"
                   class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white" />
          </div>
          <div class="flex items-end gap-2">
            <button type="button" class="dark-btn"
                    [class.dark-btn-disabled]="!custEmail || !custPassword || logging_in"
                    [disabled]="!custEmail || !custPassword || logging_in"
                    (click)="customerLogin()">
              <i class="fa-solid fa-right-to-bracket mr-1 dark:text-blue-400"></i>
              <ng-container *ngIf="!logging_in">Login</ng-container>
              <ng-container *ngIf="logging_in">Logging in…</ng-container>
            </button>
            <button type="button" class="dark-btn"
                    [class.dark-btn-disabled]="!loginReq && !custBundle"
                    [disabled]="!loginReq && !custBundle"
                    (click)="clearCustomerSession()" title="Forget the bundle + clear inputs">
              <i class="fa-solid fa-eraser mr-1"></i> Clear
            </button>
          </div>
        </div>

        <!-- Login req + res -->
        <div *ngIf="loginReq || logging_in" class="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
          <div class="border border-gray-200 dark:border-[#434863] rounded-md">
            <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
              <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Login Request (password masked)</span>
              <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" [disabled]="!loginReq" (click)="copyText(loginReq?.body)" title="Copy request body">
                <i class="fa-regular fa-copy mr-1"></i>copy
              </button>
            </div>
            <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ loginReq ? loginReq.method + ' ' + loginReq.url + '\n\n' + prettyJson(loginReq.body) : '—' }}</pre>
          </div>
          <div class="border border-gray-200 dark:border-[#434863] rounded-md">
            <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
              <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Login Response (tokens truncated)</span>
              <div class="flex items-center gap-2">
                <span *ngIf="loginRes" class="text-[10px] uppercase tracking-wide"
                      [class.text-green-500]="loginRes.ok" [class.text-red-500]="!loginRes.ok">
                  HTTP {{ loginRes.status }} · {{ loginRes.durationMs }}ms
                </span>
                <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" [disabled]="!loginRes" (click)="copyText(loginRes?.body)" title="Copy response body">
                  <i class="fa-regular fa-copy mr-1"></i>copy
                </button>
              </div>
            </div>
            <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ loginRes ? prettyJson(loginRes.body) : '—' }}</pre>
            <div *ngIf="loginRes?.error" class="px-3 pb-2 text-xs text-red-500">{{ loginRes!.error }}</div>
          </div>
        </div>

        <!-- Decoded id_token claims -->
        <div *ngIf="custBundle && decodeJwt(custBundle.id_token) as claims" class="mt-3 border border-gray-200 dark:border-[#434863] rounded-md">
          <div class="px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863] flex items-center justify-between">
            <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Decoded id_token claims</span>
            <button class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" (click)="copyText(claims)">copy</button>
          </div>
          <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ prettyJson(claims) }}</pre>
        </div>
      </div>
    </div>
  </div>

  <!-- STEP 3: Pick scanner + Mint sid (renumbered from 2 after customer-first rework).
       Hidden entirely on the MTA-endpoint path — that path has its own
       Sign+Validate buttons inside Step 2 and does not mint a launch URL. -->
  <div *ngIf="!isMtaPath" class="border border-gray-200 dark:border-[#434863] rounded-md mb-4"
       [class.opacity-50]="(!isPortalPath && (!selectedScannerId || (authPath === 'customer' && !custBundle))) || (isPortalPath && !portalReady)">
    <div class="px-3 py-2 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863] flex items-center gap-2">
      <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-app-teal-100 text-white text-xs font-bold">3</span>
      <span class="text-sm font-semibold text-gray-900 dark:text-white">
        <ng-container *ngIf="!isPortalPath">Pick scanner + Mint SID</ng-container>
        <ng-container *ngIf="authPath === 'portal-token'">Sign JWT + Validate → ?token=</ng-container>
        <ng-container *ngIf="authPath === 'portal-sid'">Sign JWT + Validate → ?sid=</ng-container>
      </span>
      <span *ngIf="mintRes?.ok" class="text-xs text-green-500 ml-auto">
        <i class="fa-solid fa-check mr-1"></i>HTTP {{ mintRes!.status }} · {{ mintRes!.durationMs }}ms
      </span>
    </div>
    <div class="p-3">
      <!-- Scanner picker inline (admin/customer paths only — portal's scanner
           comes from the JWT built in Step 1). -->
      <div *ngIf="!isPortalPath" class="mb-3">
        <label class="block text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
          Scanner to mint a SID for
          <span *ngIf="selectedScanner" class="ml-2 text-green-500 normal-case">
            <i class="fa-solid fa-check mr-1"></i>{{ selectedScanner.Client }} / {{ selectedScanner.Name }}
          </span>
        </label>
        <select [(ngModel)]="selectedScannerId"
                class="w-full bg-gray-50 border border-gray-300 text-gray-900 rounded px-2 py-1.5 text-sm dark:bg-app-black-100 dark:border-[#434863] dark:text-white">
          <option [value]="''" disabled>— pick a scanner —</option>
          <option *ngFor="let s of scanners" [value]="s.Id">{{ s.Client }} / {{ s.Name }} ({{ s.Id }})</option>
        </select>
        <div *ngIf="selectedScanner" class="mt-2 text-xs text-gray-600 dark:text-gray-400 grid grid-cols-2 gap-x-4 gap-y-1">
          <div><span class="font-semibold text-gray-700 dark:text-gray-300">Id:</span> <code class="text-app-teal-100">{{ selectedScanner.Id }}</code></div>
          <div class="break-all"><span class="font-semibold text-gray-700 dark:text-gray-300">Url:</span> <code class="text-app-teal-100">{{ selectedScanner.Url || '—' }}</code></div>
        </div>
      </div>

      <!-- Portal-path: pick which stored secret signs the JWT. Defaults to
           the newest (last-pushed) — empty value sends nothing to the
           backend, which resolves to newest. Operator can override to
           exercise rotation overlap or a freshly-imported portal secret. -->
      <div *ngIf="isPortalPath && portalAvailableSecrets.length > 0"
           class="mb-3 border border-gray-200 dark:border-[#434863] rounded-md p-2 bg-gray-50 dark:bg-app-black-100">
        <label class="block text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
          <i class="fa-solid fa-key text-app-teal-100 mr-1"></i>Sign with secret
          <span class="normal-case text-gray-400">— defaults to newest</span>
        </label>
        <select [(ngModel)]="portalSigningSecretId"
                class="w-full bg-white border border-gray-300 text-gray-900 rounded px-2 py-1 text-xs font-mono dark:bg-app-black-100 dark:border-[#434863] dark:text-white">
          <option value="">— newest (default) —</option>
          <option *ngFor="let s of portalAvailableSecrets" [value]="s.id">
            {{ s.label || '(no label)' }} · fp:{{ s.fingerprint }} · len:{{ s.length }} · {{ s.created_at | date:'yyyy-MM-dd HH:mm' }}
          </option>
        </select>
      </div>

      <!-- Portal-path SECURITY: dry-run gate. Default ON — validator runs
           all checks but does NOT write Cognito / mint SID. Operator must
           explicitly turn OFF to perform a real grant. -->
      <div *ngIf="isPortalPath" class="mb-3 border rounded-md p-2"
           [class.border-yellow-400]="portalDryRun"
           [class.bg-yellow-50]="portalDryRun"
           [class.dark:bg-yellow-900]="portalDryRun"
           [class.dark:bg-opacity-20]="portalDryRun"
           [class.border-red-500]="!portalDryRun"
           [class.bg-red-50]="!portalDryRun"
           [class.dark:bg-red-900]="!portalDryRun"
           [class.dark:bg-opacity-20]="!portalDryRun">
        <label class="flex items-center gap-2 cursor-pointer text-xs font-semibold">
          <input type="checkbox" [checked]="!portalDryRun" (change)="toggleDryRun($event)" />
          <ng-container *ngIf="portalDryRun">
            <i class="fa-solid fa-shield-halved text-yellow-600 dark:text-yellow-400"></i>
            <span class="text-yellow-800 dark:text-yellow-300">Dry-run mode (safe) — no Cognito write, no SID mint</span>
          </ng-container>
          <ng-container *ngIf="!portalDryRun">
            <i class="fa-solid fa-triangle-exclamation text-red-600 dark:text-red-400"></i>
            <span class="text-red-800 dark:text-red-300">REAL GRANT MODE — will mutate {{ portalEmailSourceValue || portalEmailSourceClaim || '(target email)' }}'s Cognito record</span>
          </ng-container>
        </label>
      </div>

      <!-- Portal-path routing options — added 2026-07-08 to exercise the
           :slug overload landed on validate-portal-token + validate-
           customer-token (backend 4aabba3 + c4cc8d1) and to POST at the
           customer's own host when they have scanner_host set. -->
      <div *ngIf="isPortalPath && selectedPortalCustomer as cust"
           class="mb-3 border border-gray-200 dark:border-[#434863] rounded-md p-2 bg-gray-50 dark:bg-app-black-100 space-y-2">
        <div class="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <i class="fa-solid fa-route text-app-teal-100 mr-1"></i>Request routing
        </div>
        <!-- A. Customer host toggle. Default-enabled when the customer has
             scanner_host configured — matches the URL shape their production
             portal uses. Disabled + unchecked when scanner_host is empty. -->
        <label class="flex items-start gap-2 text-xs cursor-pointer"
               [class.opacity-60]="!cust.scanner_host">
          <input type="checkbox"
                 class="mt-0.5"
                 [(ngModel)]="usePortalCustomerHost"
                 (ngModelChange)="_saveSettings()"
                 [disabled]="!cust.scanner_host" />
          <span class="text-gray-700 dark:text-gray-300">
            <strong>Use customer host</strong>
            <span *ngIf="cust.scanner_host" class="ml-1 text-app-teal-100 font-mono">
              → https://{{ cust.scanner_host }}/rest/...
            </span>
            <span *ngIf="!cust.scanner_host" class="ml-1 text-gray-500 italic">
              — customer has no scanner_host; POSTs to admin backend
            </span>
            <span class="block text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">
              When off, POST goes to the admin backend (env.apiUrl). Turn on to exercise the customer's own DNS + edge config — same URL shape their production integration uses.
            </span>
          </span>
        </label>
        <!-- IT-F2-437 Mike 2026-10-07 cid:6718e44b: omit the `/<slug>`
             suffix so backend derives customer from the Host header
             (admin-service PR #231). Only enabled when "Use customer host"
             is on AND the customer has a scanner_host — else the request
             goes to admin.f2-tech.ai which has no customer mapping and
             the derive step returns `cannot derive customer`. -->
        <label class="flex items-start gap-2 text-xs cursor-pointer"
               [class.opacity-60]="!(usePortalCustomerHost && cust.scanner_host)">
          <input type="checkbox"
                 class="mt-0.5"
                 [(ngModel)]="omitPortalSlug"
                 (ngModelChange)="_saveSettings()"
                 [disabled]="!(usePortalCustomerHost && cust.scanner_host)" />
          <span class="text-gray-700 dark:text-gray-300">
            <strong>Omit URL slug</strong>
            <span class="ml-1 text-app-teal-100 font-mono" *ngIf="usePortalCustomerHost && cust.scanner_host">
              → /auth/validate-portal-token <span class="text-gray-500">(no /&lt;slug&gt;)</span>
            </span>
            <span *ngIf="!(usePortalCustomerHost && cust.scanner_host)" class="ml-1 text-gray-500 italic">
              — requires "Use customer host" ON and customer has scanner_host
            </span>
            <span class="block text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">
              Exercises the branded-host-no-slug shape: backend derives customer from Host / X-Forwarded-Host via Customer.domains[]. Mike 2026-10-07: both <code>/validate-portal-token/&lt;slug&gt;</code> and <code>/validate-portal-token</code> should resolve the same customer on a branded host.
            </span>
          </span>
        </label>
        <!-- B. Scanner input mode radio. JWT (default) = scanner rides in
             the signed payload; URL = scanner id lands in the request path
             via /validate-…-token/:scanner overload. When URL + no scanner
             picked → falls back to customer slug in the URL (behavior
             matches the old shape). -->
        <div class="flex items-start gap-2 text-xs">
          <div class="pt-0.5"><strong class="text-gray-700 dark:text-gray-300">Scanner via:</strong></div>
          <div class="flex flex-col gap-1">
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="portalScannerInputMode" [(ngModel)]="portalScannerInputMode" (ngModelChange)="_saveSettings()" value="jwt" />
              <span class="text-gray-700 dark:text-gray-300">JWT claim <span class="text-gray-500 italic">(default)</span> — <code class="text-app-teal-100">payload.scanner</code></span>
            </label>
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input type="radio" name="portalScannerInputMode" [(ngModel)]="portalScannerInputMode" (ngModelChange)="_saveSettings()" value="url" />
              <span class="text-gray-700 dark:text-gray-300">URL path — <code class="text-app-teal-100">/…/{{ portalScannerSlug.trim() || cust.slug }}</code></span>
            </label>
          </div>
        </div>
      </div>

      <div class="flex items-end gap-2 flex-wrap mb-3">
        <button type="button" class="dark-btn"
                [class.dark-btn-disabled]="(!isPortalPath && (!selectedScannerId || minting || (authPath === 'customer' && !custBundle))) || (isPortalPath && (!portalReady || minting || signing_token))"
                [disabled]="(!isPortalPath && (!selectedScannerId || minting || (authPath === 'customer' && !custBundle))) || (isPortalPath && (!portalReady || minting || signing_token))"
                (click)="mintSid()">
          <i class="fa-solid fa-key mr-1 dark:text-amber-400"></i>
          <ng-container *ngIf="!minting && !signing_token">
            <ng-container *ngIf="!isPortalPath">Mint SID</ng-container>
            <ng-container *ngIf="isPortalPath && portalDryRun">Validate (dry-run)</ng-container>
            <ng-container *ngIf="isPortalPath && !portalDryRun">Grant Permissions (REAL)</ng-container>
          </ng-container>
          <ng-container *ngIf="signing_token">Signing JWT…</ng-container>
          <ng-container *ngIf="minting && !signing_token">
            <ng-container *ngIf="!isPortalPath">Minting…</ng-container>
            <ng-container *ngIf="isPortalPath && portalDryRun">Validating…</ng-container>
            <ng-container *ngIf="isPortalPath && !portalDryRun">Granting…</ng-container>
          </ng-container>
        </button>
        <!-- Hidden when the chosen validate endpoint returns ?token= (the
             customer's SPA verifies the JWT with the shared secret rather
             than redeeming a sid). Tracks the chosen authPath so sid-mode
             tests on a token_query customer still surface redeem. -->
        <label *ngIf="!isPortalPath || authPath === 'portal-sid'"
               class="ml-2 text-xs flex items-center gap-1.5 text-gray-700 dark:text-gray-300 cursor-pointer">
          <input type="checkbox" [(ngModel)]="redeemAlsoTest" [disabled]="isPortalPath && portalDryRun" />
          also redeem (single-use — invalidates the sid)
        </label>
        <span *ngIf="authPath === 'portal-token'"
              class="ml-2 text-[11px] text-gray-500 dark:text-gray-400 italic">
          <i class="fa-solid fa-info-circle mr-1"></i>token mode — no sid to redeem; customer SPA verifies the JWT signature instead.
        </span>
      </div>

      <!-- Mint req + res -->
      <div *ngIf="mintReq" class="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div class="border border-gray-200 dark:border-[#434863] rounded-md">
          <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
            <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Mint Request</span>
            <div class="flex items-center gap-2">
              <!-- JWT freshness countdown. Reflects the backend's iat-age
                   window (or explicit exp when the customer signs one) so
                   the operator can tell at a glance whether re-copying is
                   safe or the token needs to be re-signed. Mike 2026-07-08. -->
              <span *ngIf="portalTokenExpiresAtMs !== null"
                    class="text-[10px] font-mono px-1.5 py-0.5 rounded-sm"
                    [class.text-green-600]="!portalTokenExpired && portalTokenRemainingSeconds > 10"
                    [class.dark:text-green-400]="!portalTokenExpired && portalTokenRemainingSeconds > 10"
                    [class.text-amber-600]="!portalTokenExpired && portalTokenRemainingSeconds <= 10"
                    [class.dark:text-amber-400]="!portalTokenExpired && portalTokenRemainingSeconds <= 10"
                    [class.text-red-600]="portalTokenExpired"
                    [class.dark:text-red-400]="portalTokenExpired"
                    [class.bg-red-50]="portalTokenExpired"
                    [class.dark:bg-red-900]="portalTokenExpired"
                    [class.dark:bg-opacity-30]="portalTokenExpired"
                    [attr.title]="portalTokenExpired ? 'Token expired — re-sign before mint' : 'Token freshness remaining (backend rejects on iat too old)'">
                <ng-container *ngIf="!portalTokenExpired">
                  <i class="fa-regular fa-clock mr-0.5"></i>{{ portalTokenRemainingSeconds }}s
                </ng-container>
                <ng-container *ngIf="portalTokenExpired">
                  <i class="fa-solid fa-hourglass-end mr-0.5"></i>EXPIRED
                </ng-container>
              </span>
              <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" (click)="copyText(mintReq?.body)" title="Copy request body">
                <i class="fa-regular fa-copy mr-1"></i>copy
              </button>
              <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" (click)="copyCurl(mintReq)">copy as curl</button>
            </div>
          </div>
          <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ mintReq.method + ' ' + mintReq.url + '\n' + 'withCredentials: ' + mintReq.withCredentials + '\n\n' + prettyJson(mintReq.body) }}</pre>
        </div>
        <div class="border border-gray-200 dark:border-[#434863] rounded-md">
          <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
            <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Mint Response</span>
            <div class="flex items-center gap-2">
              <span *ngIf="mintRes" class="text-[10px] uppercase tracking-wide"
                    [class.text-green-500]="mintRes.ok" [class.text-red-500]="!mintRes.ok">
                HTTP {{ mintRes.status }} · {{ mintRes.durationMs }}ms
              </span>
              <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" [disabled]="!mintRes" (click)="copyResponseBundle(mintRes, mintReq)" title="Copy status + body + error (if present)">
                <i class="fa-regular fa-copy mr-1"></i>copy
              </button>
            </div>
          </div>
          <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ mintRes ? prettyJson(mintRes.body) : '—' }}</pre>
          <div *ngIf="mintRes?.error" class="px-3 pb-2 text-xs text-red-500">{{ mintRes!.error }}</div>
          <!-- IT-F2-437 Mike 2026-10-07 cid:39780fcd: HTTP 0 is a browser-
               side fetch failure (no response from server) — chaseable
               causes enumerated inline so operators can self-diagnose
               instead of pinging admin-service / f2-session each time. -->
          <div *ngIf="mintRes && mintRes.status === 0" class="px-3 pb-3 text-[11px] text-amber-600 dark:text-amber-400 border-t border-amber-200/40 dark:border-amber-800/40 bg-amber-50/40 dark:bg-amber-900/10">
            <div class="pt-2 font-semibold">HTTP 0 — browser never got a response. Common causes:</div>
            <ul class="list-disc list-inside mt-1 space-y-0.5">
              <li><strong>Cached CORS preflight failure</strong> (most common). Chrome caches a failed OPTIONS for 86400s. Fix: devtools open → Network tab → check <em>Disable cache</em> → hard reload (<kbd>Ctrl+Shift+R</kbd>). Or try a fresh browser profile / private window.</li>
              <li><strong>Vercel WAF challenge not solved</strong> on this origin yet. Visit the branded host root in the same tab, let the challenge solve, then retry.</li>
              <li><strong>DNS / alias just moved</strong>. Give it 60–120s for your resolver to pick up; retry.</li>
              <li><strong>Backend truly unreachable</strong>. If curl works but the browser doesn't, it's one of the three above — not a server-side bug.</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Redeem panel -->
      <div *ngIf="redeemReq || redeeming" class="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
        <div class="border border-gray-200 dark:border-[#434863] rounded-md">
          <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
            <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Redeem Request</span>
            <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" [disabled]="!redeemReq" (click)="copyText(redeemReq?.body)" title="Copy request body">
              <i class="fa-regular fa-copy mr-1"></i>copy
            </button>
          </div>
          <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ redeemReq ? redeemReq.method + ' ' + redeemReq.url + '\n\n' + prettyJson(redeemReq.body) : '—' }}</pre>
        </div>
        <div class="border border-gray-200 dark:border-[#434863] rounded-md">
          <div class="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863]">
            <span class="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Redeem Response</span>
            <div class="flex items-center gap-2">
              <span *ngIf="redeemRes" class="text-[10px] uppercase tracking-wide"
                    [class.text-green-500]="redeemRes.ok" [class.text-red-500]="!redeemRes.ok">
                HTTP {{ redeemRes.status }} · {{ redeemRes.durationMs }}ms
              </span>
              <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100" [disabled]="!redeemRes" (click)="copyResponseBundle(redeemRes, redeemReq)" title="Copy status + body + error (if present)">
                <i class="fa-regular fa-copy mr-1"></i>copy
              </button>
            </div>
          </div>
          <pre class="p-3 text-[11px] font-mono leading-snug text-gray-800 dark:text-gray-200 overflow-x-auto whitespace-pre">{{ redeemRes ? prettyJson(redeemRes.body) : '—' }}</pre>
          <div *ngIf="redeemRes?.error" class="px-3 pb-2 text-xs text-red-500">{{ redeemRes!.error }}</div>
          <div *ngIf="redeemRes && redeemRes.status === 0" class="px-3 pb-3 text-[11px] text-amber-600 dark:text-amber-400 border-t border-amber-200/40 dark:border-amber-800/40 bg-amber-50/40 dark:bg-amber-900/10">
            <div class="pt-2 font-semibold">HTTP 0 — browser never got a response. Common causes:</div>
            <ul class="list-disc list-inside mt-1 space-y-0.5">
              <li><strong>Cached CORS preflight failure</strong>. Fix: devtools open → Network tab → check <em>Disable cache</em> → hard reload (<kbd>Ctrl+Shift+R</kbd>). Or a fresh browser profile.</li>
              <li><strong>Vercel WAF challenge not solved</strong> on this origin. Visit the branded host root in the same tab, let the challenge solve, then retry.</li>
              <li><strong>DNS / alias just moved</strong>. 60–120s for your resolver to update.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- STEP 4: Use the URL — open / copy / embed. Token-mode shows the
       outbound JWT decoded so the operator can confirm what F2 emitted.
       Hidden on the MTA-endpoint path — that endpoint returns an identity
       bundle, not a launch URL. -->
  <div *ngIf="!isMtaPath" class="border border-gray-200 dark:border-[#434863] rounded-md mb-4"
       [class.opacity-50]="!mintRes?.ok">
    <div class="px-3 py-2 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863] flex items-center gap-2">
      <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-app-teal-100 text-white text-xs font-bold">4</span>
      <span class="text-sm font-semibold text-gray-900 dark:text-white">
        <ng-container *ngIf="returnedHandoffMode === 'token_query'">Use the URL (JWT in <code>?token=</code>) — open or embed</ng-container>
        <ng-container *ngIf="returnedHandoffMode !== 'token_query'">Use the SID — open or embed</ng-container>
      </span>
      <span *ngIf="iframeSafeSrc" class="text-xs text-green-500 ml-auto"><i class="fa-solid fa-check mr-1"></i>iframe loaded</span>
    </div>
    <div class="p-3">
      <div *ngIf="parsedUrl() as p" class="text-xs grid grid-cols-3 gap-x-4 gap-y-1 mb-3">
        <div><span class="font-semibold text-gray-700 dark:text-gray-300">Host:</span> <code class="text-app-teal-100">{{ p.host }}</code></div>
        <div><span class="font-semibold text-gray-700 dark:text-gray-300">Path:</span> <code class="text-app-teal-100">{{ p.pathname }}</code></div>
        <div class="break-all" *ngIf="returnedHandoffMode !== 'token_query'">
          <span class="font-semibold text-gray-700 dark:text-gray-300">sid:</span> <code class="text-app-teal-100">{{ p.sid }}</code>
          <button class="ml-1 text-[10px] text-gray-500 hover:text-app-teal-100" (click)="copyText(p.sid)">copy</button>
        </div>
        <div class="break-all" *ngIf="returnedHandoffMode === 'token_query'">
          <span class="font-semibold text-gray-700 dark:text-gray-300">token (JWT):</span>
          <span class="text-amber-500 ml-1">{{ p.sid?.length || 0 }} chars</span>
          <button class="ml-1 text-[10px] text-gray-500 hover:text-app-teal-100" (click)="copyText(p.sid)">copy</button>
        </div>
      </div>

      <!-- Outbound JWT preview — token_query mode only. Shows the decoded
           claims F2 emitted (HS256 signed with the customer's shared secret).
           Operator confirms email/scanner/exp landed as expected. -->
      <div *ngIf="returnedHandoffMode === 'token_query' && outboundJwtPayload"
           class="mb-3 border border-amber-300 dark:border-amber-700/60 rounded-md bg-amber-50 dark:bg-amber-900/20">
        <div class="px-3 py-1.5 border-b border-amber-300 dark:border-amber-700/60 flex items-center justify-between">
          <span class="text-xs font-semibold text-amber-900 dark:text-amber-200">
            <i class="fa-solid fa-key mr-1"></i>Outbound JWT payload (HS256, signed with customer's shared secret)
          </span>
          <button type="button" class="text-[10px] text-amber-700 dark:text-amber-300 hover:text-app-teal-100"
                  (click)="copyText(outboundJwtJson)"
                  title="Copy decoded JWT payload">
            <i class="fa-regular fa-copy mr-1"></i>copy
          </button>
        </div>
        <pre class="p-3 text-[11px] font-mono leading-snug text-amber-900 dark:text-amber-200 overflow-x-auto whitespace-pre">{{ outboundJwtJson }}</pre>
      </div>
      <div class="flex items-end gap-2 flex-wrap">
        <!-- Open Tab + Embed Iframe consume the single-use sid. Both
             disable once either is clicked (sidConsumed) OR the sid TTL
             elapses (sidExpired). Copy URL stays enabled (copying doesn't
             consume the sid). Re-mint via Sign + Validate (Step 2) resets. -->
        <button type="button" class="dark-btn"
                [class.dark-btn-disabled]="sidButtonsDisabled"
                [disabled]="sidButtonsDisabled"
                (click)="openInNewTab()" title="Open the URL in a new tab — consumes the sid">
          <i class="fa-solid fa-arrow-up-right-from-square mr-1 dark:text-blue-400"></i> Open Tab
        </button>
        <button type="button" class="dark-btn"
                [class.dark-btn-disabled]="!mintRes?.body?.url"
                [disabled]="!mintRes?.body?.url"
                (click)="copyText(mintRes?.body?.url)"
                title="Copy the SID URL to clipboard — does NOT consume the sid">
          <i class="fa-solid fa-copy mr-1 dark:text-blue-400"></i> Copy URL
        </button>
        <button type="button" class="dark-btn"
                [class.dark-btn-disabled]="sidButtonsDisabled || (isPortalPath && (!portalReady || minting || signing_token))"
                [disabled]="sidButtonsDisabled || (isPortalPath && (!portalReady || minting || signing_token))"
                (click)="embedInIframe()" title="Load the URL in the iframe below — consumes the sid">
          <i class="fa-solid fa-window-maximize mr-1 dark:text-blue-400"></i> Embed Iframe
        </button>
        <!-- Token-mode round-trip simulator. Available only when the URL
             carries a `?token=` identity JWT (handoff_mode === 'token_query').
             POSTs the JWT to /auth/exchange-portal-token — the same call the
             customer's SPA makes on landing — and shows the returned `?sid=`
             URL the SPA would then navigate to. Does NOT consume the source
             URL (identity-mode JWT is re-exchangeable as long as iat is
             fresh); just exercises the round-trip. -->
        <button *ngIf="returnedHandoffMode === 'token_query'"
                type="button" class="dark-btn"
                [class.dark-btn-disabled]="exchanging"
                [disabled]="exchanging"
                (click)="exchangeToken()"
                title="POST the JWT to /auth/exchange-portal-token — simulate the consumer SPA. Returns a ?sid= URL.">
          <i class="fa-solid fa-right-left mr-1 dark:text-amber-400"></i>
          <ng-container *ngIf="!exchanging">Exchange Token &rarr; SID URL (simulate consumer SPA)</ng-container>
          <ng-container *ngIf="exchanging">Exchanging&hellip;</ng-container>
        </button>
        <!-- Live SID-lifecycle indicator: countdown when valid, reason
             text when disabled. Helps the operator know why the buttons
             grayed out without guessing. -->
        <span *ngIf="mintRes?.body?.url" class="text-[11px] ml-2"
              [ngClass]="sidConsumed
                ? 'text-gray-400'
                : (sidExpired ? 'text-red-500' : (sidRemainingSeconds <= 10 ? 'text-amber-500' : 'text-gray-500'))">
          <ng-container *ngIf="sidConsumed">
            <i class="fa-solid fa-lock mr-1"></i>
            <ng-container *ngIf="returnedHandoffMode === 'token_query'">token URL opened &mdash; re-mint for a fresh token</ng-container>
            <ng-container *ngIf="returnedHandoffMode !== 'token_query'">sid consumed &mdash; re-mint to use again</ng-container>
          </ng-container>
          <ng-container *ngIf="!sidConsumed && sidExpired">
            <i class="fa-solid fa-clock mr-1"></i>
            <ng-container *ngIf="returnedHandoffMode === 'token_query'">token <code>exp</code> reached &mdash; re-mint for a fresh token</ng-container>
            <ng-container *ngIf="returnedHandoffMode !== 'token_query'">sid expired &mdash; re-mint to use again</ng-container>
          </ng-container>
          <ng-container *ngIf="!sidConsumed && !sidExpired">
            <i class="fa-solid fa-clock mr-1"></i>
            <ng-container *ngIf="returnedHandoffMode === 'token_query'">token valid for {{ sidRemainingSeconds }}s (JWT <code>exp</code>)</ng-container>
            <ng-container *ngIf="returnedHandoffMode !== 'token_query'">
              sid valid for {{ sidRemainingSeconds }}s
              <span *ngIf="selectedPortalCustomer?.sid_redeem_grace_seconds && selectedPortalCustomer!.sid_redeem_grace_seconds! > 0 && redeemRes?.ok"
                    class="text-blue-500 ml-1">
                (grace window &mdash; same SID still redeemable)
              </span>
            </ng-container>
          </ng-container>
        </span>
      </div>

      <!-- Exchange request + response panel — token_query mode round-trip.
           Req on left (URL + body), res on right (the `?sid=` URL F2 returns).
           Renders the moment the operator clicks Exchange Token. Body is
           shown verbatim so the operator can confirm `valid:true` plus the
           sid URL that the consumer SPA would navigate the browser to. -->
      <div *ngIf="exchangeReq || exchanging" class="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
        <div class="border border-amber-300 dark:border-amber-700/60 rounded-md">
          <div class="flex items-center justify-between px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-300 dark:border-amber-700/60">
            <span class="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wide">
              <i class="fa-solid fa-right-left mr-1"></i>Exchange Request (consumer SPA simulation)
            </span>
            <button type="button" class="text-[10px] text-amber-700 dark:text-amber-300 hover:text-app-teal-100" [disabled]="!exchangeReq" (click)="copyText(exchangeReq?.body)" title="Copy request body">
              <i class="fa-regular fa-copy mr-1"></i>copy
            </button>
          </div>
          <pre class="p-3 text-[11px] font-mono leading-snug text-amber-900 dark:text-amber-200 overflow-x-auto whitespace-pre">{{ exchangeReq ? exchangeReq.method + ' ' + exchangeReq.url + '\n\n' + prettyJson(exchangeReq.body) : '—' }}</pre>
        </div>
        <div class="border border-amber-300 dark:border-amber-700/60 rounded-md">
          <div class="flex items-center justify-between px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-300 dark:border-amber-700/60">
            <span class="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wide">Exchange Response (sid URL for SPA navigation)</span>
            <div class="flex items-center gap-2">
              <span *ngIf="exchangeRes" class="text-[10px] uppercase tracking-wide"
                    [class.text-green-500]="exchangeRes.ok" [class.text-red-500]="!exchangeRes.ok">
                HTTP {{ exchangeRes.status }} &middot; {{ exchangeRes.durationMs }}ms
              </span>
              <button type="button" class="text-[10px] text-amber-700 dark:text-amber-300 hover:text-app-teal-100" [disabled]="!exchangeRes" (click)="copyText(exchangeRes?.body)" title="Copy response body">
                <i class="fa-regular fa-copy mr-1"></i>copy
              </button>
            </div>
          </div>
          <pre class="p-3 text-[11px] font-mono leading-snug text-amber-900 dark:text-amber-200 overflow-x-auto whitespace-pre">{{ exchangeRes ? prettyJson(exchangeRes.body) : '—' }}</pre>
          <div *ngIf="exchangeRes?.error" class="px-3 pb-2 text-xs text-red-500">{{ exchangeRes!.error }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- STEP 5: Iframe (only visible when one is loaded). -->
  <div *ngIf="iframeSafeSrc" class="border border-gray-200 dark:border-[#434863] rounded-md">
    <div class="px-3 py-2 bg-gray-100 dark:bg-app-black-100 border-b border-gray-200 dark:border-[#434863] flex items-center gap-2">
      <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-app-teal-100 text-white text-xs font-bold">5</span>
      <span class="text-sm font-semibold text-gray-900 dark:text-white break-all">Iframe: {{ iframeSrc }}</span>
      <div class="flex items-center gap-2 shrink-0 ml-auto">
        <label class="text-[10px] text-gray-500 dark:text-gray-400">height
          <input type="number" min="240" max="8000" step="40" [(ngModel)]="iframeHeight"
                 class="ml-1 w-16 bg-gray-50 dark:bg-app-black-100 border border-gray-300 dark:border-[#434863] rounded px-1 text-[10px]" />
        </label>
        <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-app-teal-100"
                (click)="embedInIframe()" title="Mint a fresh sid and reload">remint</button>
        <button type="button" class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-red-400"
                (click)="closeIframe()">close</button>
      </div>
    </div>
    <iframe #iframeEl [src]="iframeSafeSrc"
            [attr.data-key]="iframeKey"
            [style.height.px]="iframeHeight"
            class="w-full block bg-white"
            referrerpolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-storage-access-by-user-activation"></iframe>
  </div>
</div>

<!-- Reuses the same ClaimsEditorComponent the Users admin grid uses. The
     editor renders one form per claims_schema.fields entry; non-scanner
     field values flow into the JWT payload (as perms + top-level claims). -->
<app-claims-editor *ngIf="claimsEditorOpen && selectedPortalCustomer"
  [customers]="claimsEditorCustomers"
  [scanners]="scanners"
  [userScanners]="(selectedPortalCustomer.scanner_ids || []).join(',')"
  [claimsJson]="claimsEditorJson"
  [title]="'Configure SSO claims — ' + selectedPortalCustomer.name"
  (save)="onClaimsEditorSave($event)"
  (close)="closePermsPopup()">
</app-claims-editor>
